// src/views/documentos/BuscarDocumentos.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { searchDocs, getDownloadUrlDoc, deleteDoc } from "../../services/api";
import LoaderPremiumLogo from "../../components/LoaderPremiumLogo";

/**
 * =========================================================
 * ✅ FRONTEND ONLY: Enterprise UX Pack (AJUSTADO A REGLA NEGOCIO)
 *
 * ✅ REGLA NEGOCIO: SOLO CERTIFICADO
 * - Preview SIEMPRE abre el PDF certificado (type=cert)
 * - Descargar SIEMPRE descarga el PDF certificado (1 solo botón)
 * - Prefetch/cache solo para certificado
 * - 409 => "Certificado en proceso" (toast)
 * - 410 => tombstone (eliminado) en localStorage
 *
 * ✅ FIX ELIMINADOS (410 Gone):
 * - Tombstones (IDs eliminados) persistentes en localStorage
 * - Si getDownloadUrlDoc devuelve 410 => markDeleted + remueve de items + limpia cache + cierra modal
 * - Filtrado al recibir resultados + filtrado en visibleItems
 * - useMemo depende de deletedIds para recalcular
 * - markDeleted actualiza deletedSetRef inmediato
 * =========================================================
 */

/* =========================
   LocalStorage keys
========================= */
const LS = {
  RECENTS: "docs.search.recents.v1",
  VIEWS: "docs.search.views.v1",
  COLS: "docs.search.columns.v1",
  DENSITY: "docs.search.density.v1",
  PRIVACY: "docs.search.privacy.v1",
  VIRT: "docs.search.virtual.v1",
  DELETED: "docs.search.deletedIds.v1", // ✅ tombstones
};

/* =========================
   Config
========================= */
const PRESIGNED_TTL_MS = 6 * 60 * 1000; // 6 min
const PREFETCH_AHEAD = 2; // 0..3 recomendado
const MAX_RECENTS = 10;
const MAX_VIEWS = 12;

/* =========================
   Helpers
========================= */
function bytesToSize(bytes = 0) {
  const units = ["B", "KB", "MB", "GB"];
  let v = Number(bytes) || 0;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function badgeForType(mime) {
  if (mime === "application/pdf")
    return { label: "PDF", cls: "bg-red-100 text-red-700 ring-red-200" };
  if (mime?.startsWith("image/"))
    return { label: "IMG", cls: "bg-blue-100 text-blue-700 ring-blue-200" };
  return { label: "FILE", cls: "bg-gray-100 text-gray-700 ring-gray-200" };
}

function statusBadge(status) {
  const map = {
    INDEXED: "bg-gray-100 text-gray-700 ring-gray-200",
    UPLOADED: "bg-gray-100 text-gray-700 ring-gray-200",
    OCR_STARTED: "bg-yellow-100 text-yellow-800 ring-yellow-200",
    OCR_DONE: "bg-blue-100 text-blue-800 ring-blue-200",
    TEXT_READY: "bg-green-100 text-green-800 ring-green-200",
    PROCESSED: "bg-purple-100 text-purple-800 ring-purple-200",
    CERT_READY: "bg-emerald-100 text-emerald-800 ring-emerald-200",
    ERROR: "bg-red-100 text-red-700 ring-red-200",
    DELETED: "bg-gray-200 text-gray-700 ring-gray-300",
  };
  const cls = map[status] || "bg-gray-100 text-gray-700 ring-gray-200";
  return { cls, label: status || "—" };
}

function safeDateLabel(iso) {
  try {
    if (!iso) return "-";
    return new Date(iso).toLocaleString("es-MX");
  } catch {
    return "-";
  }
}

function normalizeError(err) {
  if (!err) return "Error desconocido";
  if (err.response) {
    const status = err.response.status;
    const msg =
      err.response.data?.message ||
      err.response.data?.error ||
      err.response.data?.msg ||
      JSON.stringify(err.response.data || {});
    return `HTTP ${status}: ${msg}`;
  }
  if (err.message) return err.message;
  return String(err);
}

function loadLS(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function saveLS(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

function uniqBy(arr, getKey) {
  const seen = new Set();
  const out = [];
  for (const x of arr) {
    const k = getKey(x);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(x);
  }
  return out;
}

function maskId(id) {
  const s = String(id || "");
  if (!s) return "—";
  if (s.length <= 6) return "••••••";
  return `${s.slice(0, 3)}••••••${s.slice(-3)}`;
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

/* =========================
   Toast
========================= */
function Toast({ toast, onClose }) {
  if (!toast) return null;

  const tone =
    toast.type === "success"
      ? "bg-green-600"
      : toast.type === "warning"
      ? "bg-yellow-600"
      : "bg-red-600";

  return (
    <div className="fixed right-4 top-4 z-[9999]">
      <div
        className={`min-w-[280px] max-w-[420px] rounded-2xl ${tone} px-4 py-3 text-white shadow-lg`}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold">{toast.title || "Aviso"}</div>
            <div className="mt-1 text-sm opacity-95">{toast.message}</div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg bg-white/15 px-2 py-1 text-xs font-semibold hover:bg-white/25"
            title="Cerrar"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

function LoadingOverlay({ show, text = "Buscando documentos…" }) {
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-white/70 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <LoaderPremiumLogo />
        <div className="text-sm font-semibold text-gray-700">{text}</div>
        <div className="text-xs text-gray-500">
          Esto puede tardar unos segundos según el OCR.
        </div>
      </div>
    </div>
  );
}

/* =========================
   Skeleton
========================= */
function TableSkeleton({ rows = 7, dense = false }) {
  return (
    <div className="mt-3 overflow-hidden rounded-2xl ring-1 ring-gray-200">
      <div className="bg-white">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className={`flex gap-3 border-b last:border-b-0 ${
              dense ? "p-3" : "p-4"
            }`}
          >
            <div
              className={`rounded-lg bg-gray-100 animate-pulse ${
                dense ? "h-7 w-9" : "h-8 w-10"
              }`}
            />
            <div className="flex-1">
              <div className="h-4 w-2/3 rounded bg-gray-100 animate-pulse" />
              <div className="mt-2 h-3 w-1/2 rounded bg-gray-100 animate-pulse" />
            </div>
            <div className="hidden md:block h-4 w-24 rounded bg-gray-100 animate-pulse" />
            <div className="hidden md:block h-4 w-24 rounded bg-gray-100 animate-pulse" />
            <div className="hidden md:block h-8 w-44 rounded-xl bg-gray-100 animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* =========================
   Focus trap hook (modal)
========================= */
function useFocusTrap(open, containerRef, onClose) {
  useEffect(() => {
    if (!open) return;

    const root = containerRef?.current;
    if (!root) return;

    const selector =
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

    const focusables = () =>
      Array.from(root.querySelectorAll(selector)).filter(
        (el) => el.offsetParent !== null
      );

    const prevActive = document.activeElement;

    const list = focusables();
    if (list[0]) list[0].focus();

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();

      if (e.key === "Tab") {
        const f = focusables();
        if (f.length === 0) return;

        const first = f[0];
        const last = f[f.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      try {
        if (prevActive && prevActive.focus) prevActive.focus();
      } catch {
        // ignore
      }
    };
  }, [open, containerRef, onClose]);
}

/* =========================
   Scroll lock (modal open)
========================= */
function useScrollLock(locked) {
  useEffect(() => {
    if (!locked) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [locked]);
}

/* =========================
   Optional PDF Viewer:
========================= */
function PdfViewer({ url, heightClass = "h-[85vh] md:h-[72vh]" }) {
  const [mode, setMode] = useState("loading"); // loading | reactpdf | iframe
  const [ReactPdf, setReactPdf] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const mod = await import("react-pdf");
        if (!mounted) return;

        try {
          const pdfjs = mod.pdfjs;
          if (
            pdfjs?.GlobalWorkerOptions &&
            !pdfjs.GlobalWorkerOptions.workerSrc
          ) {
            pdfjs.GlobalWorkerOptions.workerSrc =
              "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";
          }
        } catch {
          // ignore
        }

        setReactPdf(mod);
        setMode("reactpdf");
      } catch {
        if (!mounted) return;
        setMode("iframe");
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  if (mode === "reactpdf" && ReactPdf) {
    const { Document, Page } = ReactPdf;
    return (
      <div className={`${heightClass} w-full overflow-auto bg-white`}>
        <div className="p-3">
          <Document
            file={url}
            loading={
              <div className="py-10 text-center text-sm text-gray-600">
                Cargando PDF…
              </div>
            }
            error={
              <div className="py-10 text-center text-sm text-gray-600">
                No se pudo renderizar PDF (viewer). Abriendo en iframe…
              </div>
            }
            onLoadError={() => setMode("iframe")}
          >
            <Page
              pageNumber={1}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
            <div className="mt-4 text-xs text-gray-500">
              * Si quieres: páginas, zoom, miniaturas y búsqueda dentro del PDF
              (solo frontend), te lo dejo.
            </div>
          </Document>
        </div>
      </div>
    );
  }

  return (
    <iframe
      title="PDF Preview"
      src={url}
      className={`${heightClass} w-full bg-white`}
    />
  );
}

/* =========================
   Optional Virtualization:
========================= */
async function tryLoadReactWindow() {
  try {
    const mod = await import("react-window");
    return mod;
  } catch {
    return null;
  }
}

/* =========================
   Column config
========================= */
const DEFAULT_COLUMNS = [
  { key: "file", label: "Archivo", visible: true },
  { key: "project", label: "Proyecto", visible: true },
  { key: "type", label: "Tipo", visible: true },
  { key: "date", label: "Fecha", visible: true },
  { key: "status", label: "Estatus", visible: true },
  { key: "actions", label: "Acciones", visible: true },
];

function normalizeColumns(cols) {
  const byKey = new Map((cols || []).map((c) => [c.key, c]));
  const merged = DEFAULT_COLUMNS.map((c) => ({
    ...c,
    ...(byKey.get(c.key) || {}),
  }));
  const extras = (cols || []).filter(
    (c) => !DEFAULT_COLUMNS.some((d) => d.key === c.key)
  );
  return [...merged, ...extras];
}

function ColumnManager({ columns, setColumns }) {
  const move = (key, dir) => {
    const i = columns.findIndex((c) => c.key === key);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= columns.length) return;
    const copy = [...columns];
    const tmp = copy[i];
    copy[i] = copy[j];
    copy[j] = tmp;
    setColumns(copy);
  };

  const toggle = (key) => {
    setColumns(
      columns.map((c) =>
        c.key === key ? { ...c, visible: !c.visible } : c
      )
    );
  };

  return (
    <div className="rounded-2xl bg-white p-3 ring-1 ring-gray-200 shadow-sm">
      <div className="text-xs font-semibold text-gray-800">Columnas</div>
      <div className="mt-2 space-y-2">
        {columns.map((c) => (
          <div key={c.key} className="flex items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={!!c.visible}
                onChange={() => toggle(c.key)}
              />
              {c.label}
            </label>

            <div className="flex items-center gap-1">
              <button
                onClick={() => move(c.key, -1)}
                className="rounded-lg px-2 py-1 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
                title="Subir"
              >
                ↑
              </button>
              <button
                onClick={() => move(c.key, +1)}
                className="rounded-lg px-2 py-1 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
                title="Bajar"
              >
                ↓
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[11px] text-gray-400">
        Guardado en localStorage.
      </div>
    </div>
  );
}

/* =========================
   Saved Views
========================= */
function ViewsPanel({ views, onApply, onSave, onDelete }) {
  const [name, setName] = useState("");

  return (
    <div className="rounded-2xl bg-white p-3 ring-1 ring-gray-200 shadow-sm">
      <div className="text-xs font-semibold text-gray-800">Vistas guardadas</div>

      <div className="mt-2 flex gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nombre (ej: Procesados hoy)"
          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
        />
        <button
          onClick={() => {
            const n = name.trim();
            if (!n) return;
            onSave(n);
            setName("");
          }}
          className="rounded-xl bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-black"
        >
          Guardar
        </button>
      </div>

      {views.length === 0 ? (
        <div className="mt-3 rounded-xl bg-gray-50 p-3 text-sm text-gray-600">
          No hay vistas guardadas.
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {views.map((v) => (
            <div
              key={v.id}
              className="flex items-center justify-between gap-2 rounded-xl bg-gray-50 p-2"
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-800 truncate">
                  {v.name}
                </div>
                <div className="text-[11px] text-gray-500 truncate">
                  q: {v.q || "—"} · tab: {v.tab} · sort: {v.sortBy}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onApply(v)}
                  className="rounded-xl bg-white px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
                >
                  Aplicar
                </button>
                <button
                  onClick={() => onDelete(v.id)}
                  className="rounded-xl bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700"
                >
                  Borrar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-2 text-[11px] text-gray-400">
        Todo es local (solo frontend).
      </div>
    </div>
  );
}

/* =========================
   Recents
========================= */
function Recents({ recents, onPick, onClear }) {
  if (!recents?.length) return null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <div className="text-xs text-gray-500">Recientes:</div>
      {recents.map((r) => (
        <button
          key={r}
          onClick={() => onPick(r)}
          className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200"
          title="Usar búsqueda"
        >
          {r}
        </button>
      ))}
      <button
        onClick={onClear}
        className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50"
      >
        Limpiar
      </button>
    </div>
  );
}

/* =========================
   Chips
========================= */
function Chip({ label, tone = "gray" }) {
  const tones = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    red: "bg-red-100 text-red-700 ring-red-200",
    blue: "bg-blue-100 text-blue-700 ring-blue-200",
    purple: "bg-purple-100 text-purple-700 ring-purple-200",
  };
  const cls = tones[tone] || tones.gray;
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ${cls}`}
    >
      {label}
    </span>
  );
}

/* =========================
   Preview Modal
========================= */
function PreviewModal({
  open,
  onClose,
  preview,
  previewLoading,
  deletingId,
  onCopyId,
  onDownload,
  onDelete,
  onPrev,
  onNext,
  canPrev,
  canNext,
  privacyMode,
}) {
  const dialogRef = useRef(null);
  useScrollLock(open);
  useFocusTrap(open, dialogRef, onClose);

  if (!open) return null;

  const isDeleted = preview?.status === "DELETED";
  const isDeleting = deletingId && preview?.documentId === deletingId;

  const ct = preview?.contentType || "";
  const typeBadge = badgeForType(ct);
  const st = statusBadge(preview?.status);

  return (
    <div className="fixed inset-0 z-[10000]">
      <div
        className="absolute inset-0 bg-black/55"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="absolute inset-0 flex items-center justify-center p-3 md:p-6">
        <div
          ref={dialogRef}
          className="w-full max-w-6xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/10"
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3 md:flex-row md:items-start md:justify-between md:px-6">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-sm font-semibold text-gray-900 break-words">
                  {preview?.fileName || "Vista previa"}
                </div>
                <span
                  className={`inline-flex items-center rounded-lg px-2 py-1 text-[11px] font-semibold ring-1 ${typeBadge.cls}`}
                >
                  {typeBadge.label}
                </span>
              </div>

              <div className="mt-1 text-xs text-gray-500">
                {preview?.projectName || "—"} · {preview?.docType || "—"} ·{" "}
                {bytesToSize(preview?.sizeBytes || 0)} ·{" "}
                {safeDateLabel(preview?.uploadedAt)}
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-lg px-2 py-1 text-xs font-medium ring-1 ${st.cls}`}
                >
                  {st.label}
                </span>
                <span className="text-[11px] text-gray-400">
                  ID:{" "}
                  {privacyMode
                    ? maskId(preview?.documentId)
                    : preview?.documentId || "—"}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={onPrev}
                disabled={!canPrev}
                className={`rounded-xl px-3 py-2 text-xs font-semibold ring-1 ${
                  canPrev
                    ? "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50"
                    : "bg-gray-100 text-gray-400 ring-gray-200 cursor-not-allowed"
                }`}
                title="Anterior (←)"
              >
                ← Anterior
              </button>

              <button
                onClick={onNext}
                disabled={!canNext}
                className={`rounded-xl px-3 py-2 text-xs font-semibold ring-1 ${
                  canNext
                    ? "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50"
                    : "bg-gray-100 text-gray-400 ring-gray-200 cursor-not-allowed"
                }`}
                title="Siguiente (→)"
              >
                Siguiente →
              </button>

              {!!preview?.documentId && (
                <button
                  onClick={() => onCopyId(preview.documentId)}
                  className="rounded-xl bg-white px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
                >
                  Copiar ID
                </button>
              )}

              {!!preview?.url && !isDeleted && (
                <button
                  onClick={() =>
                    window.open(preview.url, "_blank", "noopener,noreferrer")
                  }
                  className="rounded-xl bg-white px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
                >
                  Abrir
                </button>
              )}

              {/* ✅ SOLO CERT: un solo botón */}
              {!isDeleted && (
                <button
                  onClick={() => onDownload(preview, "cert")}
                  className="rounded-xl bg-gray-900 px-3 py-2 text-xs font-semibold text-white hover:bg-black"
                  title="Descargar PDF certificado"
                >
                  Descargar PDF
                </button>
              )}

              {!isDeleted && (
                <button
                  disabled={isDeleting}
                  onClick={() => onDelete(preview)}
                  className={`rounded-xl px-3 py-2 text-xs font-semibold text-white ${
                    isDeleting
                      ? "bg-red-300 cursor-not-allowed"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  {isDeleting ? "Eliminando…" : "Eliminar"}
                </button>
              )}

              <button
                onClick={onClose}
                className="rounded-xl bg-white px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
              >
                Cerrar (Esc)
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="bg-gray-50 relative">
            {!isDeleted && !previewLoading && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.06] select-none">
                <div className="text-5xl font-black text-gray-900 rotate-[-24deg]">
                  {new Date().toLocaleDateString("es-MX")} ·{" "}
                  {privacyMode
                    ? "CONFIDENCIAL"
                    : preview?.documentId || "CONFIDENCIAL"}
                </div>
              </div>
            )}

            {isDeleted ? (
              <div className="p-6 text-sm text-gray-700">
                Este documento fue eliminado y ya no está disponible.
              </div>
            ) : previewLoading ? (
              <div className="flex h-[85vh] md:h-[72vh] w-full items-center justify-center text-sm text-gray-600">
                Cargando vista previa…
              </div>
            ) : String(preview?.contentType || "").includes("pdf") ? (
              <PdfViewer url={preview?.url} />
            ) : (
              <div className="flex h-[85vh] md:h-[72vh] w-full items-center justify-center bg-gray-50 p-2">
                <img
                  alt="Preview"
                  src={preview?.url}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 px-4 py-2 md:px-6">
            <div className="text-[11px] text-gray-400">
              Atajos: Esc cerrar · ←/→ cambiar doc · ↑/↓ navegar tabla · / buscar
              · Enter refrescar/abrir
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================
   Tab Button
========================= */
function TabButton({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-xl px-3 py-2 text-sm font-semibold ring-1 transition ${
        active
          ? "bg-gray-900 text-white ring-gray-900"
          : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50"
      }`}
    >
      {label}
    </button>
  );
}

/* =========================
   Normal Table (sin libs)
========================= */
function NormalTable({
  rows,
  density,
  selectedIndex,
  onSelectIndex,
  onRowClickIndex,
  privacyMode,
  columns,
  onDownload,
  onDelete,
  onCopy,
  deletingId,
  loading,
}) {
  const dense = density === "compact";
  const pad = dense ? "py-2 px-3" : "py-3 px-3";
  const show = (key) => columns.some((c) => c.key === key && c.visible);

  return (
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-white z-10">
        <tr className="border-b text-left text-xs text-gray-500">
          {show("file") && <th className="py-2 px-3">Archivo</th>}
          {show("project") && <th className="py-2 px-3">Proyecto</th>}
          {show("type") && <th className="py-2 px-3">Tipo</th>}
          {show("date") && <th className="py-2 px-3">Fecha</th>}
          {show("status") && <th className="py-2 px-3">Estatus</th>}
          {show("actions") && <th className="py-2 px-3">Acciones</th>}
        </tr>
      </thead>

      <tbody>
        {rows.map((d, i) => {
          const ct = d.contentType || d.s3ContentType || "";
          const typeBadge = badgeForType(ct);
          const st = statusBadge(d.status);

          const isDeleted = d.status === "DELETED";
          const isDeleting = deletingId === d.documentId;
          const disableActions = isDeleted || isDeleting;
          const disableDelete = isDeleting || loading;

          const isSelected = i === selectedIndex;

          return (
            <tr
              key={d.sk || d.documentId || i}
              className={`border-b last:border-b-0 align-top transition ${
                isDeleted ? "opacity-60" : "cursor-pointer hover:bg-gray-50"
              } ${isSelected ? "bg-gray-50" : ""}`}
              onClick={() => {
                onSelectIndex(i);
                if (!isDeleted) onRowClickIndex(i);
              }}
              title={isDeleted ? "Documento eliminado" : "Clic para previsualizar"}
            >
              {show("file") && (
                <td className={pad}>
                  <div className="flex items-start gap-2">
                    <span
                      className={`mt-0.5 inline-flex items-center rounded-lg px-2 py-1 text-xs font-semibold ring-1 ${typeBadge.cls}`}
                    >
                      {typeBadge.label}
                    </span>

                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 break-words">
                        {d.fileName || "—"}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500">
                        {bytesToSize(d.sizeBytes || d.s3ContentLength || 0)} ·{" "}
                        {ct || "—"}
                      </div>
                      <div className="mt-0.5 text-[11px] text-gray-400 break-words">
                        ID:{" "}
                        {privacyMode
                          ? maskId(d.documentId)
                          : d.documentId || "—"}
                      </div>
                    </div>
                  </div>
                </td>
              )}

              {show("project") && <td className={pad}>{d.projectName || "—"}</td>}
              {show("type") && <td className={pad}>{d.docType || "—"}</td>}
              {show("date") && (
                <td className={`${pad} whitespace-nowrap`}>
                  {safeDateLabel(d.uploadedAt)}
                </td>
              )}

              {show("status") && (
                <td className={pad}>
                  <span
                    className={`rounded-lg px-2 py-1 text-xs font-medium ring-1 ${st.cls}`}
                  >
                    {st.label}
                  </span>
                </td>
              )}

              {show("actions") && (
                <td className={pad} onClick={(e) => e.stopPropagation()}>
                  <div className="flex flex-wrap gap-2">
                    <button
                      disabled={disableActions}
                      onClick={() => !disableActions && onRowClickIndex(i)}
                      className={`rounded-xl px-3 py-2 text-xs font-semibold ring-1 ${
                        disableActions
                          ? "bg-gray-100 text-gray-400 ring-gray-200 cursor-not-allowed"
                          : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      Ver
                    </button>

                    {/* ✅ SOLO CERT */}
                    <button
                      disabled={disableActions}
                      onClick={() => !disableActions && onDownload(d, "cert")}
                      className={`rounded-xl px-3 py-2 text-xs font-semibold ${
                        disableActions
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-gray-900 text-white hover:bg-black"
                      }`}
                      title="Descargar PDF certificado"
                    >
                      Descargar PDF
                    </button>

                    <button
                      disabled={disableDelete}
                      onClick={() => onDelete(d)}
                      className={`rounded-xl px-3 py-2 text-xs font-semibold text-white ${
                        disableDelete
                          ? "bg-red-300 cursor-not-allowed"
                          : "bg-red-600 hover:bg-red-700"
                      }`}
                    >
                      {isDeleting ? "Eliminando…" : "Eliminar"}
                    </button>

                  
                  </div>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/* =========================
   Virtualized List (react-window)
========================= */
function VirtualizedList({
  reactWindow,
  rows,
  density,
  selectedIndex,
  setSelectedIndex,
  onRowClickIndex,
  privacyMode,
  columns,
  onDownload,
  onDelete,
  onCopy,
  deletingId,
  loading,
}) {
  const { FixedSizeList } = reactWindow;
  const dense = density === "compact";
  const rowH = dense ? 72 : 86;

  const show = (key) => columns.some((c) => c.key === key && c.visible);

  const header = (
    <div className="sticky top-0 z-10 bg-white border-b text-xs text-gray-500">
      <div className="flex">
        {show("file") && (
          <div className="px-3 py-2 w-[420px] shrink-0">Archivo</div>
        )}
        {show("project") && (
          <div className="px-3 py-2 w-[180px] shrink-0">Proyecto</div>
        )}
        {show("type") && (
          <div className="px-3 py-2 w-[140px] shrink-0">Tipo</div>
        )}
        {show("date") && (
          <div className="px-3 py-2 w-[170px] shrink-0">Fecha</div>
        )}
        {show("status") && (
          <div className="px-3 py-2 w-[140px] shrink-0">Estatus</div>
        )}
        {show("actions") && (
          <div className="px-3 py-2 flex-1 min-w-[420px]">Acciones</div>
        )}
      </div>
    </div>
  );

  const Row = ({ index, style }) => {
    const d = rows[index];
    const ct = d.contentType || d.s3ContentType || "";
    const typeBadge = badgeForType(ct);
    const st = statusBadge(d.status);

    const isDeleted = d.status === "DELETED";
    const isDeleting = deletingId === d.documentId;
    const disableActions = isDeleted || isDeleting;
    const disableDelete = isDeleting || loading;
    const isSelected = index === selectedIndex;

    return (
      <div
        style={style}
        className={`border-b last:border-b-0 ${
          isDeleted ? "opacity-60" : "cursor-pointer hover:bg-gray-50"
        } ${isSelected ? "bg-gray-50" : "bg-white"}`}
        onClick={() => {
          setSelectedIndex(index);
          if (!isDeleted) onRowClickIndex(index);
        }}
        title={isDeleted ? "Documento eliminado" : "Clic para previsualizar"}
      >
        <div className={`flex items-start ${dense ? "py-2" : "py-3"}`}>
          {show("file") && (
            <div className="px-3 w-[420px] shrink-0">
              <div className="flex items-start gap-2">
                <span
                  className={`mt-0.5 inline-flex items-center rounded-lg px-2 py-1 text-xs font-semibold ring-1 ${typeBadge.cls}`}
                >
                  {typeBadge.label}
                </span>
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 break-words">
                    {d.fileName || "—"}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500">
                    {bytesToSize(d.sizeBytes || d.s3ContentLength || 0)} ·{" "}
                    {ct || "—"}
                  </div>
                  <div className="mt-0.5 text-[11px] text-gray-400 break-words">
                    ID:{" "}
                    {privacyMode
                      ? maskId(d.documentId)
                      : d.documentId || "—"}
                  </div>
                </div>
              </div>
            </div>
          )}

          {show("project") && (
            <div className="px-3 w-[180px] shrink-0 text-sm text-gray-800">
              {d.projectName || "—"}
            </div>
          )}
          {show("type") && (
            <div className="px-3 w-[140px] shrink-0 text-sm text-gray-800">
              {d.docType || "—"}
            </div>
          )}
          {show("date") && (
            <div className="px-3 w-[170px] shrink-0 text-sm text-gray-800">
              {safeDateLabel(d.uploadedAt)}
            </div>
          )}

          {show("status") && (
            <div className="px-3 w-[140px] shrink-0">
              <span
                className={`rounded-lg px-2 py-1 text-xs font-medium ring-1 ${st.cls}`}
              >
                {st.label}
              </span>
            </div>
          )}

          {show("actions") && (
            <div
              className="px-3 flex-1 min-w-[420px]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-wrap gap-2">
                <button
                  disabled={disableActions}
                  onClick={() => !disableActions && onRowClickIndex(index)}
                  className={`rounded-xl px-3 py-2 text-xs font-semibold ring-1 ${
                    disableActions
                      ? "bg-gray-100 text-gray-400 ring-gray-200 cursor-not-allowed"
                      : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50"
                  }`}
                >
                  Ver
                </button>

                {/* ✅ SOLO CERT */}
                <button
                  disabled={disableActions}
                  onClick={() => !disableActions && onDownload(d, "cert")}
                  className={`rounded-xl px-3 py-2 text-xs font-semibold ${
                    disableActions
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-gray-900 text-white hover:bg-black"
                  }`}
                  title="Descargar PDF certificado"
                >
                  Descargar PDF
                </button>

                <button
                  disabled={disableDelete}
                  onClick={() => onDelete(d)}
                  className={`rounded-xl px-3 py-2 text-xs font-semibold text-white ${
                    disableDelete
                      ? "bg-red-300 cursor-not-allowed"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  {isDeleting ? "Eliminando…" : "Eliminar"}
                </button>

                <button
                  onClick={() => onCopy(d.documentId)}
                  className="rounded-xl bg-white px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
                >
                  Copiar ID
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      {header}
      <FixedSizeList
        height={520}
        width="100%"
        itemCount={rows.length}
        itemSize={rowH}
      >
        {Row}
      </FixedSizeList>
    </div>
  );
}

/* =========================
   Main Component
========================= */
export default function BuscarDocumentos() {
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("uploadedAtDesc");
  const [tab, setTab] = useState("all");

  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [deletingId, setDeletingId] = useState(null);

  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  const searchInputRef = useRef(null);

  // prefs
  const [density, setDensity] = useState(loadLS(LS.DENSITY, "normal")); // normal | compact
  const [privacyMode, setPrivacyMode] = useState(loadLS(LS.PRIVACY, false));
  const [columns, setColumns] = useState(() =>
    normalizeColumns(loadLS(LS.COLS, DEFAULT_COLUMNS))
  );
  const [recents, setRecents] = useState(loadLS(LS.RECENTS, []));
  const [views, setViews] = useState(loadLS(LS.VIEWS, []));
  const [showOpsPanel, setShowOpsPanel] = useState(false);

  // virtualization optional
  const [reactWindow, setReactWindow] = useState(null);
  const [useVirtual, setUseVirtual] = useState(loadLS(LS.VIRT, false));

  // presigned cache (solo cert)
  const presignedCacheRef = useRef(new Map());

  // ✅ Tombstones: IDs eliminados (frontend-only)
  const [deletedIds, setDeletedIds] = useState(() => loadLS(LS.DELETED, []));
  const deletedSetRef = useRef(new Set(deletedIds));

  useEffect(() => {
    deletedSetRef.current = new Set(deletedIds);
    saveLS(LS.DELETED, deletedIds);
  }, [deletedIds]);

  const markDeleted = (id) => {
    if (!id) return;
    deletedSetRef.current.add(id);
    setDeletedIds((prev) => {
      if (prev.includes(id)) return prev;
      return [id, ...prev].slice(0, 500);
    });
  };

  const showToast = (type, message, title) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type, message, title });
    toastTimerRef.current = setTimeout(() => setToast(null), 3200);
  };

  // Persist prefs
  useEffect(() => saveLS(LS.DENSITY, density), [density]);
  useEffect(() => saveLS(LS.PRIVACY, privacyMode), [privacyMode]);
  useEffect(() => saveLS(LS.COLS, columns), [columns]);
  useEffect(() => saveLS(LS.RECENTS, recents), [recents]);
  useEffect(() => saveLS(LS.VIEWS, views), [views]);
  useEffect(() => saveLS(LS.VIRT, useVirtual), [useVirtual]);

  // ✅ Load optional libs (solo 1 vez)
  useEffect(() => {
    (async () => {
      const rw = await tryLoadReactWindow();
      if (rw) setReactWindow(rw);
    })();
  }, []);

  const addRecent = (value) => {
    const v = (value || "").trim();
    if (!v) return;
    const next = uniqBy([v, ...recents], (x) => x).slice(0, MAX_RECENTS);
    setRecents(next);
  };

  const saveView = (name) => {
    const view = {
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      name,
      q: (q || "").trim(),
      tab,
      sortBy,
      density,
      privacyMode,
      useVirtual,
    };
    const next = [view, ...views].slice(0, MAX_VIEWS);
    setViews(next);
    showToast("success", `Vista "${name}" guardada.`, "Vistas");
  };

  const applyView = (v) => {
    setQ(v.q || "");
    setTab(v.tab || "all");
    setSortBy(v.sortBy || "uploadedAtDesc");
    if (v.density) setDensity(v.density);
    if (typeof v.privacyMode === "boolean") setPrivacyMode(v.privacyMode);
    if (typeof v.useVirtual === "boolean") setUseVirtual(v.useVirtual);
    showToast("success", `Vista aplicada: ${v.name}`, "Vistas");
  };

  const deleteView = (id) => {
    setViews(views.filter((x) => x.id !== id));
    showToast("success", "Vista eliminada.", "Vistas");
  };

  const clearCacheForId = (documentId) => {
    if (!documentId) return;
    presignedCacheRef.current.delete(`cert:${documentId}`);
  };

  // ✅ 410 Gone handler
  const handleGone = (docId, sk) => {
    if (!docId) return;

    markDeleted(docId);
    clearCacheForId(docId);

    setItems((prev) =>
      prev.filter((x) => x.documentId !== docId && (sk ? x.sk !== sk : true))
    );

    if (preview?.documentId === docId) {
      setPreview(null);
      setPreviewOpen(false);
    }

    showToast(
      "warning",
      "Documento eliminado: este archivo ya no está disponible.",
      "Documento eliminado"
    );
  };

  const loadMore = async (reset = false) => {
    try {
      if (loading) return;

      const query = (q || "").trim();
      if (!query) {
        showToast(
          "warning",
          "Escribe algo para buscar (nombre, id, OCR, etc.).",
          "Falta búsqueda"
        );
        return;
      }

      setLoading(true);

      const params = { q: query, limit: 25 };
      if (!reset && nextCursor) params.cursor = nextCursor;

      const myReqId = ++reqIdRef.current;
      const res = await searchDocs(params);
      if (myReqId !== reqIdRef.current) return;

      const rawItems = res.data?.items || [];

      const newItems = rawItems.filter((x) => {
        const id = x?.documentId;
        if (id && deletedSetRef.current.has(id)) return false;
        if (x?.status === "DELETED") return false;
        return true;
      });

      const cursor = res.data?.nextCursor || null;

      setItems((prev) => (reset ? newItems : [...prev, ...newItems]));
      setNextCursor(cursor);
      addRecent(query);
    } catch (e) {
      showToast("error", normalizeError(e), "Error al buscar");
    } finally {
      setLoading(false);
    }
  };

  const resetAndLoad = async () => {
    setItems([]);
    setNextCursor(null);
    setPreview(null);
    setPreviewOpen(false);
    setSelectedIndex(-1);
    await loadMore(true);
  };

  const refresh = async () => {
    await resetAndLoad();
    showToast("success", "Resultados actualizados.", "Refrescar");
  };

  // typeahead
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const query = (q || "").trim();
    if (!query) {
      setItems([]);
      setNextCursor(null);
      setPreview(null);
      setPreviewOpen(false);
      setSelectedIndex(-1);
      return;
    }

    debounceRef.current = setTimeout(() => {
      resetAndLoad();
    }, 350);

    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const visibleItems = useMemo(() => {
    let arr = [...items].filter((x) => {
      const id = x?.documentId;
      if (id && deletedSetRef.current.has(id)) return false;
      if (x?.status === "DELETED") return false;
      return true;
    });

    if (tab === "error") arr = arr.filter((x) => x.status === "ERROR");
    if (tab === "processed")
      arr = arr.filter((x) => x.status === "PROCESSED" || x.status === "CERT_READY");
    if (tab === "ocr")
      arr = arr.filter(
        (x) => x.status === "OCR_DONE" || x.status === "TEXT_READY"
      );

    arr.sort((a, b) => {
      const aDate = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
      const bDate = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
      const aName = (a.fileName || "").toLowerCase();
      const bName = (b.fileName || "").toLowerCase();
      const aSize = Number(a.sizeBytes || a.s3ContentLength || 0);
      const bSize = Number(b.sizeBytes || b.s3ContentLength || 0);

      if (sortBy === "uploadedAtDesc") return bDate - aDate;
      if (sortBy === "uploadedAtAsc") return aDate - bDate;
      if (sortBy === "nameAsc") return aName.localeCompare(bName, "es");
      if (sortBy === "sizeDesc") return bSize - aSize;
      return 0;
    });

    return arr;
  }, [items, tab, sortBy, deletedIds]);

  const insights = useMemo(() => {
    const total = visibleItems.length;
    const processed = visibleItems.filter(
      (x) => x.status === "PROCESSED" || x.status === "CERT_READY"
    ).length;
    const errors = visibleItems.filter((x) => x.status === "ERROR").length;
    const ocrReady = visibleItems.filter(
      (x) => x.status === "OCR_DONE" || x.status === "TEXT_READY"
    ).length;

    const counts = new Map();
    for (const x of visibleItems) {
      const k = x.projectName || "—";
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    const topProjects = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);

    return { total, processed, errors, ocrReady, topProjects };
  }, [visibleItems]);

  // ✅ Presigned cache fetch (SOLO cert) + 409/410
  const getPresignedUrlCached = async (documentId, sk) => {
    const now = Date.now();
    const cacheKey = `cert:${documentId}`;

    const cached = presignedCacheRef.current.get(cacheKey);
    if (cached && cached.url && cached.exp && cached.exp > now) return cached.url;

    try {
      // ✅ SOLO cert
      const res = await getDownloadUrlDoc(documentId, "cert");
      const url = res.data?.url;
      if (!url) throw new Error("No llegó url de descarga");

      presignedCacheRef.current.set(cacheKey, {
        url,
        exp: now + PRESIGNED_TTL_MS,
      });

      return url;
    } catch (e) {
      const status = e?.response?.status;

      if (status === 410) {
        handleGone(documentId, sk);
        return null;
      }

      if (status === 409) {
        showToast(
          "warning",
          "El certificado aún no está listo. Intenta de nuevo en unos segundos.",
          "Certificado en proceso"
        );
        return null;
      }

      throw e;
    }
  };

  const prefetchAround = async (index) => {
    if (PREFETCH_AHEAD <= 0) return;
    const targets = [];
    for (let k = 1; k <= PREFETCH_AHEAD; k++) {
      const i = index + k;
      const doc = visibleItems[i];
      if (!doc?.documentId) continue;
      if (doc.status === "DELETED") continue;
      if (deletedSetRef.current.has(doc.documentId)) continue;
      targets.push({ id: doc.documentId, sk: doc.sk });
    }
    for (const t of targets) {
      try {
        await getPresignedUrlCached(t.id, t.sk); // ✅ cert
      } catch {
        // ignore
      }
    }
  };

  const openPreviewByIndex = async (index) => {
    const doc = visibleItems[index];
    if (!doc) return;

    if (
      doc?.status === "DELETED" ||
      deletedSetRef.current.has(doc?.documentId)
    ) {
      showToast(
        "warning",
        "Este documento fue eliminado y ya no está disponible.",
        "Documento eliminado"
      );
      return;
    }

    setSelectedIndex(index);
    setPreviewOpen(true);
    setPreviewLoading(true);

    try {
      // ✅ SIEMPRE cert
      const url = await getPresignedUrlCached(doc.documentId, doc.sk);
      if (!url) return;

      setPreview({
        documentId: doc.documentId,
        fileName: doc.fileName,
        url,
        contentType: doc.contentType || doc.s3ContentType || "",
        uploadedAt: doc.uploadedAt,
        status: doc.status,
        sizeBytes: doc.sizeBytes || doc.s3ContentLength || 0,
        docType: doc.docType,
        projectName: doc.projectName,
        sk: doc.sk,
      });

      prefetchAround(index);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 410) return;
      if (status === 409) return;
      showToast("error", normalizeError(e), "Error en vista previa");
    } finally {
      setPreviewLoading(false);
    }
  };

  // ✅ Descargar: SIEMPRE cert
  const onDownload = async (doc) => {
    try {
      if (
        doc?.status === "DELETED" ||
        deletedSetRef.current.has(doc?.documentId)
      ) {
        showToast(
          "warning",
          "Este documento fue eliminado y ya no está disponible.",
          "Documento eliminado"
        );
        return;
      }

      const url = await getPresignedUrlCached(doc.documentId, doc.sk);
      if (!url) return;

      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 410) return;
      if (status === 409) return;
      showToast("error", normalizeError(e), "Error al descargar");
    }
  };

  const onDelete = async (doc) => {
    try {
      if (!doc?.documentId) {
        showToast("error", "No llegó documentId.", "Eliminar");
        return;
      }

      const name = doc.fileName || doc.documentId;

      const ok1 = window.confirm(
        `¿Seguro que deseas ELIMINAR este documento?\n\n${name}\n\nEsto elimina el archivo del bucket.`
      );
      if (!ok1) return;

      const ok2 = window.confirm("Confirmación final: ¿Eliminar definitivamente?");
      if (!ok2) return;

      setDeletingId(doc.documentId);
      await deleteDoc(doc.documentId);

      clearCacheForId(doc.documentId);
      markDeleted(doc.documentId);

      if (preview?.documentId === doc.documentId) {
        setPreview(null);
        setPreviewOpen(false);
      }

      setItems((prev) =>
        prev.filter((x) => x.documentId !== doc.documentId && x.sk !== doc.sk)
      );

      showToast("success", "Documento eliminado correctamente.", "Eliminar");
    } catch (e) {
      showToast("error", normalizeError(e), "Error al eliminar");
    } finally {
      setDeletingId(null);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ""));
      showToast("success", "Copiado al portapapeles.", "Copiar");
    } catch {
      showToast("error", "No se pudo copiar.", "Copiar");
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = async (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const isTyping = tag === "input" || tag === "textarea" || tag === "select";

      if (
        !previewOpen &&
        e.key === "/" &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey
      ) {
        e.preventDefault();
        searchInputRef.current?.focus?.();
        return;
      }

      if (previewOpen) {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          const prevIdx = selectedIndex - 1;
          if (prevIdx >= 0) await openPreviewByIndex(prevIdx);
        }
        if (e.key === "ArrowRight") {
          e.preventDefault();
          const nextIdx = selectedIndex + 1;
          if (nextIdx < visibleItems.length) await openPreviewByIndex(nextIdx);
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setPreviewOpen(false);
        }
        return;
      }

      if (isTyping) {
        if (e.key === "Enter" && e.target === searchInputRef.current) {
          e.preventDefault();
          refresh();
        }
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (visibleItems.length === 0) return;
        setSelectedIndex((i) =>
          clamp(i < 0 ? 0 : i + 1, 0, visibleItems.length - 1)
        );
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (visibleItems.length === 0) return;
        setSelectedIndex((i) =>
          clamp(i < 0 ? 0 : i - 1, 0, visibleItems.length - 1)
        );
      }

      if (e.key === "Enter") {
        if (selectedIndex >= 0 && selectedIndex < visibleItems.length) {
          e.preventDefault();
          await openPreviewByIndex(selectedIndex);
        }
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewOpen, selectedIndex, visibleItems]);

  useEffect(() => {
    setSelectedIndex((i) => {
      if (visibleItems.length === 0) return -1;
      if (i >= visibleItems.length) return visibleItems.length - 1;
      return i;
    });
  }, [visibleItems.length]);

  const visibleCols = useMemo(() => columns.filter((c) => c.visible), [columns]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <LoadingOverlay
        show={loading && (q || "").trim().length > 0}
        text="Buscando documentos…"
      />

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        preview={preview}
        previewLoading={previewLoading}
        deletingId={deletingId}
        onCopyId={copy}
        onDownload={onDownload}
        onDelete={onDelete}
        onPrev={() => selectedIndex > 0 && openPreviewByIndex(selectedIndex - 1)}
        onNext={() =>
          selectedIndex + 1 < visibleItems.length &&
          openPreviewByIndex(selectedIndex + 1)
        }
        canPrev={selectedIndex > 0}
        canNext={selectedIndex + 1 < visibleItems.length}
        privacyMode={privacyMode}
      />

      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Buscar documentos
            </h1>

            <div className="mt-2 flex flex-wrap gap-2">
              <Chip label={`Total: ${insights.total}`} />
              <Chip label={`Procesados: ${insights.processed}`} tone="purple" />
              <Chip label={`Errores: ${insights.errors}`} tone="red" />
              <Chip label={`OCR listos: ${insights.ocrReady}`} tone="blue" />
              {insights.topProjects?.length > 0 && (
                <span className="text-xs text-gray-400 ml-1">
                  Top:{" "}
                  {insights.topProjects
                    .map(([k, v]) => `${k} (${v})`)
                    .join(" · ")}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-gray-800 ring-1 ring-gray-200 hover:bg-gray-50"
            >
              Refrescar
            </button>

            <button
              onClick={() => setShowOpsPanel((v) => !v)}
              className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black"
            >
              Preferencias
            </button>
          </div>
        </div>

        {/* Ops Panel */}
        {showOpsPanel && (
          <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="rounded-2xl bg-white p-3 ring-1 ring-gray-200 shadow-sm">
              <div className="text-xs font-semibold text-gray-800">
                Operación (solo UI)
              </div>

              <div className="mt-2 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">Densidad</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setDensity("compact")}
                      className={`rounded-xl px-3 py-2 text-xs font-semibold ring-1 ${
                        density === "compact"
                          ? "bg-gray-900 text-white ring-gray-900"
                          : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      Compacta
                    </button>
                    <button
                      onClick={() => setDensity("normal")}
                      className={`rounded-xl px-3 py-2 text-xs font-semibold ring-1 ${
                        density === "normal"
                          ? "bg-gray-900 text-white ring-gray-900"
                          : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      Normal
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">Privacidad (IDs)</div>
                  <button
                    onClick={() => setPrivacyMode((v) => !v)}
                    className={`rounded-xl px-3 py-2 text-xs font-semibold ring-1 ${
                      privacyMode
                        ? "bg-gray-900 text-white ring-gray-900"
                        : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50"
                    }`}
                    title="Enmascara IDs en pantalla (copiar sigue copiando el real)"
                  >
                    {privacyMode ? "Activada" : "Desactivada"}
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">Virtualización</div>
                  <button
                    onClick={() => {
                      if (!reactWindow) {
                        showToast(
                          "warning",
                          "Instala react-window para activar virtualización. Se queda OFF.",
                          "Virtualización"
                        );
                        setUseVirtual(false);
                        return;
                      }
                      setUseVirtual((v) => !v);
                    }}
                    className={`rounded-xl px-3 py-2 text-xs font-semibold ring-1 ${
                      useVirtual
                        ? "bg-gray-900 text-white ring-gray-900"
                        : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50"
                    }`}
                    title={
                      reactWindow
                        ? "Optimiza rendimiento con muchas filas"
                        : "react-window no instalado"
                    }
                  >
                    {useVirtual ? "ON" : "OFF"}
                  </button>
                </div>

                <button
                  onClick={() => setDeletedIds([])}
                  className="mt-2 rounded-xl bg-white px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
                  title="Vuelve a mostrar docs si el backend los regresa (solo UI)"
                >
                  Limpiar eliminados (UI)
                </button>
              </div>
            </div>

            <ColumnManager columns={columns} setColumns={setColumns} />
            <ViewsPanel
              views={views}
              onApply={applyView}
              onSave={saveView}
              onDelete={deleteView}
            />
          </div>
        )}

        {/* Search box */}
        <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-7">
            <input
              ref={searchInputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar… (ej: valdes, mauricio, 10143663, curp, factura, etc.)"
              className="md:col-span-3 rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
            />

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="md:col-span-2 rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
            >
              <option value="uploadedAtDesc">Orden: Fecha (nuevos primero)</option>
              <option value="uploadedAtAsc">Orden: Fecha (viejos primero)</option>
              <option value="nameAsc">Orden: Nombre (A → Z)</option>
              <option value="sizeDesc">Orden: Tamaño (grandes primero)</option>
            </select>

            <button
              onClick={() => {
                setTab("all");
                setQ("");
                setSortBy("uploadedAtDesc");
                setItems([]);
                setNextCursor(null);
                setSelectedIndex(-1);
              }}
              className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
            >
              Limpiar
            </button>

            <button
              onClick={() =>
                saveView(`Vista ${new Date().toLocaleString("es-MX")}`)
              }
              className="rounded-xl bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-black"
              title="Guarda la vista actual"
            >
              Guardar vista
            </button>
          </div>

          <Recents
            recents={recents}
            onPick={(r) => setQ(r)}
            onClear={() => setRecents([])}
          />

          {/* Tabs */}
          <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2">
              <TabButton
                active={tab === "all"}
                onClick={() => setTab("all")}
                label="Todos"
              />
              <TabButton
                active={tab === "error"}
                onClick={() => setTab("error")}
                label="Solo errores"
              />
              <TabButton
                active={tab === "processed"}
                onClick={() => setTab("processed")}
                label="Procesados"
              />
              <TabButton
                active={tab === "ocr"}
                onClick={() => setTab("ocr")}
                label="OCR listos"
              />
            </div>
          </div>
        </div>

        {/* Resultados */}
        <div className="mt-6 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
          <div className="mb-2">
            <div className="text-sm font-semibold text-gray-900">Resultados</div>
          </div>

          {loading && items.length === 0 ? (
            <TableSkeleton rows={7} dense={density === "compact"} />
          ) : visibleItems.length === 0 ? (
            <div className="mt-4 rounded-xl bg-gray-50 p-6 text-center text-sm text-gray-600">
              {q.trim()
                ? "Sin resultados. Prueba otra palabra."
                : "Escribe en la caja de búsqueda para comenzar."}
            </div>
          ) : (
            <div className="mt-3 overflow-auto rounded-2xl ring-1 ring-gray-200">
              {useVirtual && reactWindow ? (
                <VirtualizedList
                  reactWindow={reactWindow}
                  rows={visibleItems}
                  density={density}
                  selectedIndex={selectedIndex}
                  setSelectedIndex={setSelectedIndex}
                  onRowClickIndex={openPreviewByIndex}
                  privacyMode={privacyMode}
                  columns={visibleCols}
                  onDownload={onDownload}
                  onDelete={onDelete}
                  onCopy={copy}
                  deletingId={deletingId}
                  loading={loading}
                />
              ) : (
                <NormalTable
                  rows={visibleItems}
                  density={density}
                  selectedIndex={selectedIndex}
                  onSelectIndex={setSelectedIndex}
                  onRowClickIndex={openPreviewByIndex}
                  privacyMode={privacyMode}
                  columns={visibleCols}
                  onDownload={onDownload}
                  onDelete={onDelete}
                  onCopy={copy}
                  deletingId={deletingId}
                  loading={loading}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}